import { DateOnly } from '@if/core/date'
import { t } from '@if/core/i18n'
import { ToggleButton, ToggleButtonGroup } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import ServerErrorsAlert from '@src/components/ServerErrorsAlert'
import { useUser } from '@src/contexts/AppContext'
import { useMerchantById, useMerchantByParentId } from '@src/data/api/merchants-api/merchants-api'
import {
  MerchantUsersStatsRequestDto,
  useGetMerchantUsers,
  useManageUserProfile,
} from '@src/data/api/user-api/user-api'
import { Merchant } from '@src/types/Merchant'
import { User } from '@src/types/User'
import { useEffect, useState } from 'react'
import ManageUserDialog from './ManageUserDialog'
import { useManageUsersColumns } from './manageUsers-hooks'

const getDateValue = (months: number): DateOnly => {
  if (months === 0) return new DateOnly('1970-01-01')
  const now = new DateOnly()
  now.setMonth(now.getMonth() - months)
  return now
}

const ManageUsersPage = () => {
  const [toggleValue, setToggleValue] = useState<DateOnly>(getDateValue(1))
  const currentUser = useUser()
  const [merchant] = useMerchantById(currentUser?.merchantId ?? '')
  const [subsideries] = useMerchantByParentId(
    currentUser?.isBannerUser && currentUser?.merchantId ? currentUser.merchantId : '',
  )
  const merchantIds: string[] = [merchant, ...(subsideries ?? [])]
    .filter((m): m is Merchant => m !== null)
    .map((m) => m.id)

  const dto: MerchantUsersStatsRequestDto = { merchantIds, createdOnDateLimit: toggleValue }
  const [merchantUsers, isLoading, usersError] = useGetMerchantUsers(dto)
  const [manageUser] = useManageUserProfile()
  const [users, setUsers] = useState<User[]>([])

  // Sync with usersRaw
  useEffect(() => {
    const mappedUsers = (merchantUsers ?? []).map((user) => ({
      ...user,
      id: user.id,
    }))
    setUsers(mappedUsers)
  }, [merchantUsers])

  const [updatingUserEmail, setUpdatingUserEmail] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [isActivating, setIsActivating] = useState(false)


  const handleManageAccountClick = (user: User) => {
    setSelectedUser(user)
    setIsActivating(!user.accountEnabled)
    setDialogOpen(true)
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
    setSelectedUser(null)
  }

  const handleDialogConfirm = async () => {
    setDialogOpen(false)
    setSelectedUser(null)
    if (selectedUser) {
      setUpdatingUserEmail(selectedUser.email)
      const updatedUser = await manageUser({ ...selectedUser, accountEnabled: !selectedUser.accountEnabled })
      setUsers((prev) => prev.map((u) => (u.email === selectedUser.email ? { ...updatedUser, id: updatedUser.id } : u)))
      setUpdatingUserEmail(null)
    }
  }

  const handleToggleChange = (_: React.MouseEvent<HTMLElement>, newValue: DateOnly) => {
    setToggleValue(newValue)
  }

  const columns: GridColDef[] = useManageUsersColumns(handleManageAccountClick, updatingUserEmail)
  return (
    <div>
      <div style={{ marginBottom: 8, fontWeight: 500, fontSize: '1.1rem' }}>
        {t('UserStatsHeaders.selectStatsPeriod')}
      </div>
      <ToggleButtonGroup value={toggleValue} exclusive onChange={handleToggleChange} sx={{ mb: 2 }} size="small">
        {[1, 3, 6, 12, 0].map((months) => {
          const btnValue = getDateValue(months)
          const selected = toggleValue.toString() === btnValue.toString()
          const label =
            months === 0 ? t('common.allStats') : `${months} ${months === 1 ? t('common.month') : t('common.months')}`
          return (
            <ToggleButton
              key={months}
              value={btnValue}
              sx={
                selected
                  ? {
                      backgroundColor: 'primary.dark',
                      color: 'white',
                      fontWeight: 600,
                      '&:hover': { backgroundColor: 'primary.main' },
                    }
                  : {}
              }
            >
              {label}
            </ToggleButton>
          )
        })}
      </ToggleButtonGroup>
      {usersError && <ServerErrorsAlert serverErrors={usersError} />}
      <DataGrid
        rows={users ?? []}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 15, page: 0 },
          },
        }}
        loading={isLoading}
      />
      <ManageUserDialog
        open={dialogOpen}
        user={selectedUser}
        onClose={handleDialogClose}
        onConfirm={handleDialogConfirm}
        isActivating={isActivating}
      />
    </div>
  )
}

export default ManageUsersPage
